import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTools, faTimes } from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';

const SearchBar = ({ subservices, onResultClick }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const logTimeout = useRef(null);

  const goldColor = '#c5a059';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const logSearchQuery = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) return;
    try {
      await fetch(`${API_BASE}/api/search-log`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true' 
        },
        body: JSON.stringify({ query: searchTerm.trim() })
      });
    } catch (err) {
      console.error("Search log error:", err);
    }
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (logTimeout.current) clearTimeout(logTimeout.current);
    if (val.trim().length >= 2) {
      logTimeout.current = setTimeout(() => {
        logSearchQuery(val);
      }, 1000);
    }

    if (val.length > 0) {
      const filtered = subservices.filter(s => 
        s.name.toLowerCase().includes(val.toLowerCase())
      );
      setResults(filtered);
      setIsFocused(true);
    } else {
      setResults([]);
    }
  };

  const goToService = (res) => {
    setQuery("");
    setResults([]);
    setIsFocused(false);
    if (onResultClick) onResultClick(); 
    navigate(`/subservice/${res.id}`);
  };

  // --- Styles ---
  const containerStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: '400px',
    margin: '0 auto',
    zIndex: 2001
  };

  const searchBoxStyle = {
    display: 'flex',
    alignItems: 'center',
    background: '#f8fafc',
    border: `1px solid ${isFocused ? goldColor : '#e2e8f0'}`,
    borderRadius: '12px',
    padding: '8px 15px',
    transition: 'all 0.3s ease',
    boxShadow: isFocused ? `0 0 0 3px rgba(197, 160, 89, 0.2)` : 'none'
  };

  const inputStyle = {
    border: 'none',
    background: 'none',
    outline: 'none',
    width: '100%',
    padding: '5px 10px',
    fontSize: '15px',
    color: '#0f172a'
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '110%',
    left: 0,
    right: 0,
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    maxHeight: '300px',
    overflowY: 'auto'
  };

  const itemStyle = {
    padding: '12px 15px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    transition: 'background 0.2s',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155'
  };

  return (
    <div style={containerStyle} ref={searchRef} role="search">
      <div style={searchBoxStyle}>
        <FontAwesomeIcon icon={faSearch} style={{ color: goldColor }} aria-hidden="true" />
        <input 
          type="text" 
          // ✅ Հիմնական ուղղում Lighthouse-ի համար
          aria-label={t('search_placeholder') || "Search services"}
          placeholder={t('search_placeholder')} 
          value={query} 
          onChange={handleSearch}
          onFocus={() => query.length > 0 && setIsFocused(true)}
          style={inputStyle}
        />
        {query && (
          <button
            type="button"
            // ✅ "X" icon-ը դարձավ մատչելի կոճակ
            aria-label={t('clear_search') || "Clear search"}
            onClick={() => {
              setQuery("");
              setResults([]);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#94a3b8'
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>

      {isFocused && results.length > 0 && (
        <div style={dropdownStyle} role="listbox">
          {results.map(res => (
            <div 
              key={res.id} 
              style={itemStyle}
              role="option"
              aria-selected="false"
              tabIndex="0" // Հնարավորություն է տալիս ստեղնաշարով կանգնել վրան
              onClick={() => goToService(res)}
              onKeyDown={(e) => e.key === 'Enter' && goToService(res)}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fff7ed'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
            >
              <FontAwesomeIcon icon={faTools} style={{ color: goldColor, fontSize: '14px' }} aria-hidden="true" /> 
              <span style={{ fontSize: '14px', fontWeight: '500' }}>{res.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;